import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const TEAL = [13, 148, 136];
const GRAY = [55, 65, 81];
const GRAY_MUTED = [107, 114, 128];
const BG_TABLE_ALT = [249, 250, 251];
const MARGIN = 12;

const fmtMoney = (n) =>
  parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtQty = (n) =>
  parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: 4 });

function safeCode(code) {
  return String(code || 'guia').replace(/[^\w.-]+/g, '_');
}

function drawSectionTitle(doc, y, title) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...GRAY);
  doc.text(title, MARGIN, y);
  return y + 5;
}

function addTable(doc, startY, head, body, columnStyles = {}) {
  autoTable(doc, {
    startY,
    margin: { left: MARGIN, right: MARGIN },
    head: [head],
    body,
    theme: 'grid',
    styles: { fontSize: 7, cellPadding: 1.8, textColor: GRAY },
    headStyles: { fillColor: TEAL, textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: BG_TABLE_ALT },
    columnStyles,
  });
  return doc.lastAutoTable.finalY + 6;
}

export async function downloadProduccionGuiaPdf(informe) {
  if (!informe?.guia) throw new Error('Informe inválido');

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = MARGIN + 2;

  const { guia, meta, guiasVinculadas, elaborados, insumosPep, insumosPt, subproductosPep, coproductosPep, subproductosRecuperados, totales } =
    informe;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...TEAL);
  doc.text(meta.tipoTitulo.toUpperCase(), pageWidth / 2, y, { align: 'center' });
  y += 6;

  doc.setFontSize(9);
  doc.setTextColor(...GRAY_MUTED);
  doc.text('Informe de costos, insumos y guías vinculadas', pageWidth / 2, y, { align: 'center' });
  y += 3;
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, y, pageWidth - MARGIN, y);
  y += 7;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY);
  const metaLines = [
    `Código: ${guia.code}  ·  Fecha: ${guia.guide_date || ''} ${guia.guide_time || ''}  ·  Estado: ${guia.production_status_label}`,
    `Almacén: ${guia.warehouse_name || '—'}  ·  Proyecto: ${guia.project_name || '—'}  ·  Corte kárdex: ${meta.kardexCorte}`,
    `Recetas: ${guia.recetas_count}${guia.produccion_linea_name ? `  ·  Línea: ${guia.produccion_linea_name}` : ''}`,
  ];
  metaLines.forEach((line) => {
    doc.text(line, MARGIN, y);
    y += 4.2;
  });
  y += 2;

  if (guiasVinculadas?.length) {
    y = drawSectionTitle(doc, y, 'Guías de inventario vinculadas');
    const guideBody = guiasVinculadas.map((g) => [g.tipo, g.code]);
    y = addTable(doc, y, ['Tipo', 'Código'], guideBody, {
      0: { cellWidth: 90 },
      1: { cellWidth: 40, fontStyle: 'bold' },
    });
  }

  y = drawSectionTitle(doc, y, `Elaborados (${elaborados?.length || 0})`);
  if (elaborados?.length) {
    y = addTable(
      doc,
      y,
      ['Receta', 'Clasificación', 'Cant.', 'C. unit.', 'C. total'],
      elaborados.map((r) => [
        r.receta_name,
        r.classification,
        `${fmtQty(r.quantity)} ${r.unit}`,
        fmtMoney(r.unitPrice),
        fmtMoney(r.totalCost),
      ]),
      {
        0: { cellWidth: 42 },
        1: { cellWidth: 48 },
        2: { cellWidth: 22, halign: 'right' },
        3: { cellWidth: 22, halign: 'right' },
        4: { cellWidth: 22, halign: 'right' },
      },
    );
  } else {
    doc.setFontSize(8);
    doc.text('Sin elaborados.', MARGIN, y);
    y += 6;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(`Costo total elaborados: S/ ${fmtMoney(totales.costoElaboradosTotal)}`, pageWidth - MARGIN, y, {
    align: 'right',
  });
  y += 8;

  y = drawSectionTitle(doc, y, 'Insumos (kárdex al corte)');
  const mapInsumo = (r) => [
    r.origen || 'PT',
    r.product,
    fmtQty(r.cantidadDespacho),
    r.unit,
    fmtMoney(r.costoUnitario),
    fmtMoney(r.subtotal),
  ];

  if (totales.tienePep && insumosPep?.length) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('PEP', MARGIN, y);
    y += 4;
    y = addTable(
      doc,
      y,
      ['Or.', 'Insumo', 'Despacho', 'Ud.', 'C.u.', 'Subt.'],
      insumosPep.map(mapInsumo),
      {
        0: { cellWidth: 10 },
        1: { cellWidth: 58 },
        2: { cellWidth: 18, halign: 'right' },
        3: { cellWidth: 12 },
        4: { cellWidth: 18, halign: 'right' },
        5: { cellWidth: 20, halign: 'right' },
      },
    );
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('Producto terminado', MARGIN, y);
    y += 4;
  }

  const insumosBody = (totales.tienePep ? insumosPt : [...(insumosPep || []), ...(insumosPt || [])]) || [];
  if (insumosBody.length) {
    y = addTable(
      doc,
      y,
      ['Or.', 'Insumo', 'Despacho', 'Ud.', 'C.u.', 'Subt.'],
      insumosBody.map(mapInsumo),
      {
        0: { cellWidth: 10 },
        1: { cellWidth: 58 },
        2: { cellWidth: 18, halign: 'right' },
        3: { cellWidth: 12 },
        4: { cellWidth: 18, halign: 'right' },
        5: { cellWidth: 20, halign: 'right' },
      },
    );
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(`Costo total insumos: S/ ${fmtMoney(totales.costoInsumosTotal)}`, pageWidth - MARGIN, y, {
    align: 'right',
  });
  y += 8;

  if (subproductosPep?.length) {
    y = drawSectionTitle(doc, y, 'Subproductos PEP');
    y = addTable(
      doc,
      y,
      ['Producto', 'Cant.', 'C. unit.', 'C. total'],
      subproductosPep.map((r) => [r.product, `${fmtQty(r.quantity)} ${r.unit}`, fmtMoney(r.unitPrice), fmtMoney(r.totalCost)]),
    );
  }

  if (coproductosPep?.length) {
    y = drawSectionTitle(doc, y, 'Co-productos PEP');
    y = addTable(
      doc,
      y,
      ['Producto', 'Origen', 'Cant.', 'C. total'],
      coproductosPep.map((r) => [r.product, r.source || '—', `${fmtQty(r.quantity)} ${r.unit}`, fmtMoney(r.totalCost)]),
    );
  }

  if (subproductosRecuperados?.length) {
    y = drawSectionTitle(doc, y, 'Subproductos recuperados');
    y = addTable(
      doc,
      y,
      ['Producto', 'Cant.', 'Ud.'],
      subproductosRecuperados.map((r) => [r.product, fmtQty(r.quantity), r.unit]),
    );
  }

  if (guia.notes) {
    if (y > 250) {
      doc.addPage();
      y = MARGIN;
    }
    y = drawSectionTitle(doc, y, 'Observaciones');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const lines = doc.splitTextToSize(guia.notes, pageWidth - MARGIN * 2);
    doc.text(lines, MARGIN, y);
  }

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(...GRAY_MUTED);
    doc.text(`${guia.code} — Pág. ${i}/${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 6, {
      align: 'center',
    });
  }

  doc.save(`Produccion_${safeCode(guia.code)}.pdf`);
}
